import { matchPath } from 'react-router-dom';
import type { PermissionRequirement } from '@/types/auth';
import type { AuthIdentity } from '@/types/identity';
import { meetsRequirement } from '@/utils/permissions';

// Routes, sidebar and command navigation use this same policy. Unknown paths deny.
export const routePermissions: Record<string, PermissionRequirement | 'authenticated'> = {
  '/': { allOf: ['dashboard.read'] },
  '/dashboard': { allOf: ['dashboard.read'] },
  '/suppliers': { allOf: ['suppliers.read'] },
  '/customers': { allOf: ['customers.read'] },
  '/pricing': { allOf: ['customer_prices.read'] },
  '/pricing/customer-groups': { allOf: ['customer_prices.read'] },
  '/services': { allOf: ['services.read'] },
  '/service-lines': { allOf: ['service_lines.read'] },
  '/categories': { allOf: ['categories.read'] },
  '/categories/hierarchy': { allOf: ['categories.read'] },
  '/materials': { allOf: ['materials.read'] },
  '/cutting': { allOf: ['cutting_forms.read'] },
  '/cutting-configs': { allOf: ['cutting_configs.read'] },
  '/cutting-sizes': { allOf: ['cutting_sizes.read'] },
  '/work-pool': { anyOf: ['orders.read', 'production.read', 'delivery.read', 'cutting_forms.read'] },
  '/delivery/daily': { allOf: ['delivery.read'] },
  '/orders': { allOf: ['orders.read'] },
  '/orders/create': { allOf: ['orders.read', 'orders.create'] },
  '/invoices': { allOf: ['invoices.read'] },
  '/receipt-vouchers': { allOf: ['receipts.read'] },
  '/discount-vouchers': { allOf: ['discounts.read'] },
  '/payment-vouchers': { allOf: ['payments.read'] },
  '/cash-book': { allOf: ['cash_book.read'] },
  '/warehouse-receipts': { allOf: ['warehouse_receipts.read'] },
  '/warehouse-receipt-requests': { allOf: ['warehouse_receipt_requests.read'] },
  '/warehouse-deliveries': { allOf: ['warehouse_deliveries.read'] },
  '/warehouse-checks': { allOf: ['warehouse_checks.read'] },
  '/profile': 'authenticated',
  '/guides': 'authenticated',
  '/roles': { allOf: ['roles.read'] },
  '/users': { allOf: ['users.read'] },
  '/auth-audit': { allOf: ['audit.read'] },
  '/reports/suppliers': { allOf: ['reports.suppliers.read'] },
  '/reports/customers': { anyOf: ['reports.sales.read', 'reports.finance.read'] },
  '/reports/services': { allOf: ['reports.sales.read'] },
  '/reports/services/:serviceCode/invoices': { allOf: ['reports.sales.read'] },
  '/reports/production-errors': { allOf: ['reports.production.read'] },
  '/reports/sales': { allOf: ['reports.sales.read'] },
  '/reports/inventory': { allOf: ['reports.inventory.read'] },
  '/notifications': { allOf: ['notifications.read'] },
};

export function canAccessRoute(user: AuthIdentity | null | undefined, pathname: string): boolean {
  if (!user?.actor_id || user.must_change_password) return false;
  const entry = Object.entries(routePermissions).find(([path]) => matchPath({ path, end: true, caseSensitive: true }, pathname));
  if (!entry) return false;
  return entry[1] === 'authenticated' || meetsRequirement(user, entry[1]);
}
